import React, { memo, useCallback } from "react";
import { ListGroupItem, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { BsBellSlash, BsPinAngle } from "react-icons/bs";
import AccentIcons from "../../icons/Icons";
import { AccentTooltip } from "../../controls/AccentTooltip";
import AssigneeBadge from "../../controls/chat/AssigneeBadge";
import AssigneePickerPopover from "../../controls/chat/AssigneePickerPopover";
import StatusPill from "../../controls/chat/StatusPill";
import { format, A, t } from "../../services/HelperService";

/** Reusable “three dots” menu (same look/behavior as before) */
const InlineMoreMenu = memo(function InlineMoreMenu({ actions, disabled }) {
    const [open, setOpen] = React.useState(false);
    const toggle = () => setOpen(v => !v);
    return (
        <Dropdown isOpen={open} toggle={toggle} inNavbar={false} direction="down">
            <DropdownToggle tag="span" className="chat-inline-more" style={{ position: "relative", zIndex: open ? 1001 : 1 }}>
                <AccentIcons.More top={0} disabled={disabled} noPadding />
            </DropdownToggle>
            <DropdownMenu
                container="body"
                end
                flip
                strategy="fixed"
                modifiers={[
                    { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                    { name: "offset", options: { offset: [0, 6] } },
                    { name: "computeStyles", options: { adaptive: false, gpuAcceleration: false } }
                ]}
                className="ccv-more-menu"
                style={{ zIndex: 10001 }}
                popperClassName="ccv-more-menu-popper"
            >
                {actions.map((a, i) =>
                    a === "divider"
                        ? <div key={i} className="dropdown-divider" />
                        : <DropdownItem key={i} onClick={() => a.onClick?.()} disabled={disabled}>{a.text}</DropdownItem>
                )}
            </DropdownMenu>
        </Dropdown>
    );
});

function areEqual(prev, next) {
    return (
        prev.active === next.active &&
        prev.needsAttention === next.needsAttention &&
        prev.canEdit === next.canEdit &&
        prev.canArchive === next.canArchive &&
        prev.assigneeOpenChatId === next.assigneeOpenChatId &&
        prev.chat.ChatID === next.chat.ChatID &&
        prev.chat.Status === next.chat.Status &&
        prev.chat.IsArchived === next.chat.IsArchived &&
        prev.chat.IsPinned === next.chat.IsPinned &&
        prev.chat.IsMuted === next.chat.IsMuted &&
        prev.chat.Subject === next.chat.Subject &&
        prev.chat.ContactID === next.chat.ContactID &&
        prev.chat.ContactName === next.chat.ContactName &&
        prev.chat.RepresentativeAssigneeName === next.chat.RepresentativeAssigneeName &&
        prev.chat.TeamAssigneeName === next.chat.TeamAssigneeName &&
        prev.chat.LastActivityAt === next.chat.LastActivityAt &&
        prev.chat.LinkedJobs === next.chat.LinkedJobs
    );
}

/** One list row */
const CustomerChatRow = memo(function CustomerChatRow({
    chat,
    active,
    needsAttention,
    onSelect,
    buildMoreActions,
    assigneeOpenChatId,
    setAssigneeOpenChatId,
    onAssignToMe,
    onAssigneeCommitted,
    onStatusChange,
    ChannelIcon,
    canArchive,
    canEdit,
    selectedTextClass,
}) {
    const openAssigneeFor = useCallback((e) => {
        e?.stopPropagation?.();
        setAssigneeOpenChatId(prev => (prev === chat.ChatID ? null : chat.ChatID));
    }, [chat.ChatID, setAssigneeOpenChatId]);

    return (
        <ListGroupItem
            key={chat.ChatID}
            action
            tag="button"
            onClick={() => onSelect(chat)}
            active={active}
            className={needsAttention && !active ? "ccv-unread-row" : ""}
        >
            <div className="ccv-row-inner">
                <div className="d-flex align-items-center">
                    {canArchive && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="me-2 d-flex align-items-center justify-content-center"
                            style={{ width: 28 }}
                        >
                            <InlineMoreMenu actions={buildMoreActions(chat)} disabled={false} />
                        </div>
                    )}

                    <div className="flex-grow-1">
                        <div className="ccv-row-line d-flex gap-2 justify-content-between align-items-center">
                            <h6 className="mb-1">
                                #{chat.ID}: {chat.Subject || chat.ContactPoint}
                                {chat.IsPinned && <BsPinAngle className="ccv-muted-icon" title={t("application_strings.views.chat.row.pinned")} size={14} aria-label={t("application_strings.views.chat.row.pinned")} />}
                                {chat.IsMuted && <BsBellSlash className="ccv-muted-icon" title={t("application_strings.views.chat.row.muted")} size={14} aria-label={t("application_strings.views.chat.row.muted")} />}
                            </h6>
                            <div className="d-flex align-items-center gap-2">
                                <AssigneeBadge
                                    id={`assignee-${chat.ChatID}`}
                                    name={(chat.RepresentativeAssigneeName || chat.TeamAssigneeName || "")}
                                    kind={chat.RepresentativeAssigneeName ? "User" : (chat.TeamAssigneeName ? "Team" : null)}
                                    onClick={chat.IsArchived ? undefined : openAssigneeFor}
                                    disabled={chat.IsArchived || !canEdit}
                                />
                                <AssigneePickerPopover
                                    open={assigneeOpenChatId != null}
                                    anchorId={assigneeOpenChatId != null ? `assignee-${assigneeOpenChatId}` : undefined}
                                    chatId={assigneeOpenChatId}
                                    onClose={() => setAssigneeOpenChatId(null)}
                                    onAssignToMe={onAssignToMe}
                                    onCommitted={onAssigneeCommitted}
                                />
                                <div id={`status-${chat.ChatID}`}>
                                    <StatusPill
                                        value={chat.Status}
                                        onChange={(next) => onStatusChange(chat, next)}
                                        stopClickPropagation={true}
                                        alpha={0.8}
                                        disabled={chat.IsArchived || !canEdit}
                                    />
                                </div>
                                <>
                                    <span id={`icon-channel-${chat.ChatID}`}>
                                        <ChannelIcon channel={chat.Channel} />
                                    </span>
                                    <AccentTooltip
                                        target={`icon-channel-${chat.ChatID}`}
                                        text={chat.Channel}
                                        placement="top"
                                    />
                                </>
                            </div>
                        </div>

                        <small className={selectedTextClass}>
                            <span>
                                {chat.ContactID ? (
                                    <A
                                        className={`acc-link ${active ? 'acc-link--inverse' : ''}`}
                                        href={`/Contact/${chat.ContactID}`}
                                        title={t("application_strings.views.chat.row.openContact")}
                                    >
                                    {chat.ContactName}
                                    </A>
                                ) : (
                                        <span>{chat.ContactPointDisplayName}</span>
                                    )
                                }
                                <span> · {chat.FromDisplayName}</span>
                                {chat.IsArchived ? (
                                    <>
                                        {" "} · {" "} {t("application_strings.views.chat.row.archivedAt")} {chat.ArchivedAt && format.formatDateTime(chat.ArchivedAt)}
                                        {chat.ArchivedByRepresentativeName && <> {t("application_strings.views.chat.row.by")} {chat.ArchivedByRepresentativeName}</>}
                                    </>
                                ) : (
                                        <>{" "} · {" "} {t("application_strings.views.chat.row.lastActivityAt")} {chat.LastActivityAt ? format.formatDateTime(chat.LastActivityAt) : "—"}</>
                                )}
                            </span>
                        </small>
                    </div>
                </div>
            </div>
        </ListGroupItem>
    );
}, areEqual);

export default CustomerChatRow;
